import React, { useEffect, useState } from 'react';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import { useTranslation } from 'react-i18next';
import { LeavePrompt } from '../Prompt/LeavePrompt/LeavePrompt.component';
import { FormControl, Grid, MenuItem, Select, TextField } from '@material-ui/core';
import { Committee, committeesArray } from '../../models/committees.types';
import { FormControlLabel, FormLabel, InputLabel, Radio, RadioGroup, SelectChangeEvent } from '@mui/material';
import { userOkeId } from '../../services/users/identity/user.service';
import useSessionUser from '../../hooks/useSessionUser';
import { exportFile } from '../../utils/servicesHelper';
import { exportFinanceReport } from '../../services/finance/finance.service';
import { useLayoutContext } from '../../Context/Layout.context';
import {
  FileType,
  FileTypeE,
  FinancialReportE,
  FinancialReportType,
} from '../ExamResultsRepository/ExamResultsRepository.types';
import usePermission, { UserPermission } from '../../hooks/usePermission';

interface FinancialReportProps {
  onCancel: () => void;
  onSave: () => void;
  sheetCode: string;
  sheetId: string;
}

export const FinancialReport: React.FC<FinancialReportProps> = (props) => {
  const { t } = useTranslation();
  const { user } = useSessionUser();

  const hasAdminFinanceAccess = usePermission(UserPermission.AdminFinanceAccess);
  const hasAdminOkeFinanceAccess = usePermission(UserPermission.AdminOkeFinanceAccess);

  const commitees =
    hasAdminOkeFinanceAccess && !hasAdminFinanceAccess
      ? [committeesArray[userOkeId(user) - 1]]
      : hasAdminFinanceAccess
      ? committeesArray
      : [];

  const [fileType, setFileType] = useState<string>('');
  const [financialReportType, setFinancialReportType] = useState<string>('');
  const [fileEnum, setFileEnum] = useState<FileTypeE>(FileTypeE.csv);
  const [fileTypeString, setFileTypeString] = useState<FileType>(FileType.csv);
  const [financialReportString, setFinancialReportString] = useState<FinancialReportType>(FinancialReportType.tasks);
  const [financialReportEnum, setFinancialReportEnum] = useState<FinancialReportE>(FinancialReportE.tasks);
  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [commitee, setCommitee] = useState(commitees[0]?.id.toString() ?? '');

  const { showLoader, openAlert } = useLayoutContext();

  const handleChange = (event: SelectChangeEvent) => {
    setCommitee(event as unknown as string);
  };

  const handleCancel = () => {
    props.onCancel();
    setIsLeavePromptOpen(false);
  };

  useEffect(() => {
    if (fileType === FileType.csv) {
      setFileEnum(FileTypeE.csv);
      setFileTypeString(FileType.csv);
    }
    if (fileType === FileType.json) {
      setFileEnum(FileTypeE.json);
      setFileTypeString(FileType.json);
    }
    if (financialReportType === FinancialReportType.tasks) {
      setFinancialReportEnum(FinancialReportE.tasks);
      setFinancialReportString(FinancialReportType.tasks);
    }
    if (financialReportType === FinancialReportType.RGZ) {
      setFinancialReportEnum(FinancialReportE.RGZ);
      setFinancialReportString(FinancialReportType.RGZ);
    }
  }, [fileEnum, fileType, financialReportType, financialReportEnum]);

  const handleExportResults = () => {
    showLoader(true);
    exportFinanceReport(props.sheetId, commitee, fileEnum, financialReportEnum)
      .then(
        (response: Blob) => {
          const fileName =
            financialReportType && financialReportType === FinancialReportType.RGZ
              ? `RozliczenieRGZ_${props.sheetCode}_${commitee}`
              : '';
          exportFile(response, 'rozliczenie_', fileTypeString, fileName);
          openAlert('success', 'Dane zostały pomyślnie wyeksportowane.');
        },
        () => {
          openAlert('error', 'Wystąpił błąd podczas generowania dokumentacji. Spróbuj ponownie później');
        },
      )
      .finally(() => showLoader(false));
  };

  const handleChangeFileType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileType((event.target as HTMLInputElement).value);
  };

  const handleFinancialReportType = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFinancialReportType((event.target as HTMLInputElement).value);
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="sm"
        title="Rozliczenia finansowe"
        autoHeight
        onCancel={handleCancel}
        onConfirm={handleExportResults}
        confirmButtonDisabled={(commitees.length !== 1 && !commitee) || fileType === '' || financialReportType === ''}
        handleClose={() => setIsLeavePromptOpen(true)}
        confirmButtonLabel={'Generuj'}
      >
        <Grid xs={12} style={{ margin: '80px' }}>
          <Grid item xs={10} style={{ marginTop: '10px', marginBottom: '20px' }}>
            <InputLabel id="select-outlined-label" style={{ marginBottom: '10px', fontWeight: 600, color: '#2C2D3E' }}>
              Komisja egzaminacyjna:
            </InputLabel>
            {commitees.length === 1 ? (
              <TextField
                disabled
                value={commitees[0].name}
                id="outlined-textarea"
                placeholder="Wybierz komisję egzaminacyjną"
                variant="outlined"
                inputProps={{
                  readOnly: true,
                  style: {
                    padding: '15.5px',
                    width: '300px',
                  },
                }}
              />
            ) : (
              <FormControl variant="outlined">
                <Select
                  value={commitee ?? ''}
                  onChange={(e) => handleChange(e.target.value as SelectChangeEvent)}
                  labelId="select-outlined-label"
                  id="select-outlined"
                  placeholder="Wybierz komisję egzaminacyjną"
                  SelectDisplayProps={{
                    style: {
                      padding: '15.5px',
                      width: '300px',
                    },
                  }}
                >
                  {commitees.map((item: { name: string; id: Committee }) => (
                    <MenuItem disableRipple key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Grid>
          <Grid item xs={10}>
            <FormControl>
              <FormLabel id="rodzaj-pliku" style={{ fontWeight: 600, color: '#2C2D3E' }}>
                Rodzaj pliku:{' '}
              </FormLabel>
              <RadioGroup
                aria-labelledby="rodzaj-pliku"
                name="rodzaj-pliku"
                value={fileType}
                onChange={handleChangeFileType}
                style={{ display: 'flex', flexDirection: 'row' }}
              >
                <FormControlLabel value={FileType.json} control={<Radio />} label="json" />
                <FormControlLabel value={FileType.csv} control={<Radio />} label="CSV" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={10}>
            <FormControl>
              <FormLabel id="opcja-rozliczenia" style={{ fontWeight: 600, color: '#2C2D3E', paddingTop: '8px' }}>
                Opcja rozliczania:{' '}
              </FormLabel>
              <RadioGroup
                aria-labelledby="opcja-rozliczenia"
                name="opcja-rozliczenia"
                value={financialReportType}
                onChange={handleFinancialReportType}
                style={{ display: 'flex', flexDirection: 'row' }}
              >
                <FormControlLabel value={FinancialReportType.tasks} control={<Radio />} label="zadaniami" />
                <FormControlLabel value={FinancialReportType.RGZ} control={<Radio />} label="RGZ" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={handleCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
